import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";
import ProcessComplaintModal from "./ProcessComplaintModal";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaPhone,
  FaStore,
  FaMapMarkerAlt,
  FaClipboardCheck,
  FaImage,
  FaUserShield,
  FaClock,
  FaTimes,
  FaExpandAlt,
  FaTools,
  FaReply,
  FaTasks,
  FaCheckCircle,
  FaBan,
  FaExternalLinkAlt,
} from "react-icons/fa";

export default function ComplaintDetailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { backEnd_request } = BackMethods();

  const [loading, setLoading] = useState(true);
  const [complaint, setComplaint] = useState(null);
  const [visitResults, setVisitResults] = useState(null);
  const [selectedImg, setSelectedImg] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processLoading, setProcessLoading] = useState(false);

  const formatDateTime = (value) => {
    if (!value) return "غير متوفر";
    return new Date(value).toLocaleString("ar-SY");
  };

  const getMissionStatusMeta = (status) => {
    switch (status) {
      case 1:
        return {
          label: "قيد الانتظار",
          className: "bg-warning-subtle text-warning border border-warning",
          icon: <FaClock className="ms-1" />,
        };
      case 2:
        return {
          label: "تم الإنجاز",
          className: "bg-success-subtle text-success border border-success",
          icon: <FaCheckCircle className="ms-1" />,
        };
      case 3:
        return {
          label: "ملغاة",
          className: "bg-danger-subtle text-danger border border-danger",
          icon: <FaBan className="ms-1" />,
        };
      default:
        return {
          label: "غير معروف",
          className:
            "bg-secondary-subtle text-secondary border border-secondary",
          icon: <FaTasks className="ms-1" />,
        };
    }
  };

  const fetchDetails = async () => {
    const uid = new URLSearchParams(location.search).get("uid");
    if (!uid) return;
    try {
      const [complaintRes, visitResultsRes] = await Promise.all([
        backEnd_request.get(`/public/staff/complaints/${uid}`),
        backEnd_request.get(`/public/staff/complaints/${uid}/visit-results`),
      ]);

      setComplaint(complaintRes.data);
      setVisitResults(visitResultsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [location.search]);

  const handleProcess = async (formData) => {
    setProcessLoading(true);
    try {
      await backEnd_request.post(
        `/public/complaints/${complaint.complaintUid}/process`,
        {
          response: formData.response,
          markPublic: formData.markPublic,
          complaintCategoryId: formData.complaintCategoryId,
        },
      );
      setShowProcessModal(false);
      fetchDetails();
    } catch (err) {
      console.log(err);
      const message =
        err.response?.data?.message ||
        err.response?.data?.title ||
        "حدث خطأ أثناء معالجة الشكوى";
      alert(message);
    } finally {
      setProcessLoading(false);
    }
  };

  if (loading)
    return (
      <div className="text-center py-5 mt-5">
        <div className="spinner-border text-primary shadow-sm"></div>
      </div>
    );
  if (!complaint) return null;

  const missionsById = Object.fromEntries(
    (complaint.missions || []).map((m) => [m.missionId, m]),
  );
  const missionResults = (
    visitResults?.results?.length
      ? visitResults.results
      : complaint.missions || []
  ).map((result) => {
    const fromComplaint = missionsById[result.missionId];
    if (!fromComplaint) return result;
    return {
      ...fromComplaint,
      ...result,
      assignedToUsername:
        result.assignedToUsername || fromComplaint.assignedToUsername,
      assignedToFirstName:
        result.assignedToFirstName || fromComplaint.assignedToFirstName,
      assignedToFatherName:
        result.assignedToFatherName || fromComplaint.assignedToFatherName,
      assignedToLastName:
        result.assignedToLastName || fromComplaint.assignedToLastName,
      assignedAccount: result.assignedAccount || fromComplaint.assignedAccount,
    };
  });
  const missionCount = missionResults.length;
  const establishment = complaint.establishment;
  const hasPhotos = complaint.photos?.length > 0;
  const establishmentPath = establishment?.fieldUID
    ? `/admin/establishments/${establishment.fieldUID}`
    : null;

  const getAssignedName = (result) =>
    result?.assignedToUsername ||
    result?.assignedAccount?.username ||
    [
      result?.assignedToFirstName,
      result?.assignedToFatherName,
      result?.assignedToLastName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    "";

  return (
    <div className="bg-light min-vh-100 py-4" dir="rtl">
      <div className="container shadow-container">
        {/* Header */}
        <div className="complaint-top-bar bg-white p-3 p-md-4 rounded-4 shadow-sm border-end border-primary border-5 mb-4">
          <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3">
            <div className="flex-grow-1 min-w-0">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <h4 className="fw-bold mb-0 text-dark">تفاصيل ملف الشكوى</h4>
                <span
                  className={`badge rounded-pill px-3 py-2 complaint-status-pill ${complaint.isProcessed ? "is-processed" : "is-pending"}`}
                >
                  {complaint.isProcessed ? (
                    <>
                      <FaClipboardCheck className="ms-1" /> تمت المعالجة
                    </>
                  ) : (
                    <>
                      <FaClock className="ms-1" /> قيد المراجعة
                    </>
                  )}
                </span>
              </div>
              {establishment && (
                <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                  <span className="text-muted small fw-semibold">
                    الفعالية:
                  </span>
                  {establishmentPath ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none establishment-link fw-bold"
                      onClick={() => navigate(establishmentPath)}
                    >
                      <FaStore className="ms-1 text-primary" />
                      {establishment.name}
                      <FaExternalLinkAlt
                        className="me-1 opacity-75"
                        size={11}
                      />
                    </button>
                  ) : (
                    <span className="fw-bold text-dark">
                      {establishment.name}
                    </span>
                  )}
                  {(establishment.governorate || establishment.city) && (
                    <span className="text-muted small">
                      <FaMapMarkerAlt className="ms-1" />
                      {[
                        establishment.governorate,
                        establishment.city,
                        establishment.district,
                      ]
                        .filter(Boolean)
                        .join(" — ")}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="d-flex flex-wrap gap-2 justify-content-xl-end">
              <button
                type="button"
                className="btn btn-primary rounded-pill px-4 shadow-sm fw-bold border-0"
                onClick={() =>
                  navigate("/admin/inspection-mission/create", {
                    state: {
                      fieldUID: establishment?.fieldUID,
                      establishmentName: establishment?.name,
                      complaintUid: complaint.complaintUid,
                    },
                  })
                }
              >
                <FaTools className="ms-2" /> إنشاء مهمة
              </button>
              {!complaint.isProcessed && (
                <button
                  type="button"
                  className="btn btn-success rounded-pill px-4 shadow-sm fw-bold border-0"
                  onClick={() => setShowProcessModal(true)}
                >
                  <FaReply className="ms-2" /> معالجة الشكوى
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="btn btn-white shadow-sm rounded-pill px-4 border"
              >
                <FaArrowRight className="ms-2" /> العودة للسجل
              </button>
            </div>
          </div>
        </div>

        <div className="row g-3">
          <div className={hasPhotos ? "col-lg-7" : "col-12"}>
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100">
              <div className="card-header bg-white py-2 px-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <FaClipboardCheck className="text-primary ms-1" />
                  <h5 className="mb-0 fw-bold text-dark">محتوى البلاغ</h5>
                  {complaint.submitterPhone && (
                    <span className="submitter-phone-badge" dir="ltr">
                      <FaPhone className="me-1" />
                      {complaint.submitterPhone}
                    </span>
                  )}
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="badge bg-primary-subtle text-primary border border-primary rounded-pill px-2 py-1 small">
                    <FaTasks className="ms-1" />
                    {missionCount} مهمة
                  </span>
                  {visitResults?.hasPendingMissions && (
                    <span className="badge bg-warning-subtle text-warning border border-warning rounded-pill px-2 py-1 small">
                      مهام معلّقة
                    </span>
                  )}
                </div>
              </div>
              <div className="card-body p-3 bg-white">
                <blockquote className="complaint-quote p-3 rounded-3 position-relative mb-0">
                  <p className="text-dark lh-lg mb-0 complaint-text">
                    {complaint.complaintText}
                  </p>
                </blockquote>
                <div className="d-flex flex-wrap gap-2 gap-md-3 mt-3 pt-3 border-top text-muted small">
                  <span>
                    <FaCalendarAlt className="ms-1 text-primary" />
                    التقديم:{" "}
                    <strong>{formatDateTime(complaint.createdAtUtc)}</strong>
                  </span>
                  {complaint.isProcessed && (
                    <span className="text-success">
                      <FaClipboardCheck className="ms-1" />
                      المعالجة:{" "}
                      <strong>
                        {formatDateTime(complaint.processedAtUtc)}
                      </strong>
                    </span>
                  )}
                  {complaint.isProcessed && complaint.username && (
                    <span>
                      <FaUserShield className="ms-1" />
                      المعالِج: <strong>{complaint.username}</strong>
                    </span>
                  )}
                </div>
                {complaint.isProcessed && (
                  <div className="mt-3 pt-3 border-top">
                    {(complaint.complaintCategoryName ||
                      complaint.categoryName ||
                      complaint.complaintCategory?.name) && (
                      <div className="mb-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <FaClipboardCheck className="text-primary" />
                          <span className="fw-bold small text-dark">تصنيف الشكوى</span>
                        </div>
                        <span className="badge bg-primary-subtle text-primary border border-primary rounded-pill px-3 py-2">
                          {complaint.complaintCategoryName ||
                            complaint.categoryName ||
                            complaint.complaintCategory?.name}
                        </span>
                      </div>
                    )}
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <FaReply className="text-success" />
                      <span className="fw-bold small text-dark">
                        رد المعالجة
                      </span>
                    </div>
                    <div className="p-3 rounded-3 bg-success-subtle border border-success border-opacity-10 text-dark lh-base small">
                      {complaint.staffResponse || "تم إغلاق الشكوى بنجاح."}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {hasPhotos && (
            <div className="col-lg-5">
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100">
                <div className="card-header bg-white py-2 px-3 border-bottom">
                  <h5 className="mb-0 fw-bold">
                    <FaImage className="ms-2 text-info" />
                    المرفقات ({complaint.photos.length})
                  </h5>
                </div>
                <div className="card-body p-3">
                  <div className="row g-2">
                    {complaint.photos.map((photo, i) => (
                      <div key={i} className="col-6">
                        <div
                          className="image-card image-card--compact shadow-sm rounded-3 overflow-hidden"
                          onClick={() => setSelectedImg(photo.url)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) =>
                            e.key === "Enter" && setSelectedImg(photo.url)
                          }
                        >
                          <img
                            src={photo.url}
                            alt={`مرفق ${i + 1}`}
                            className="img-fluid"
                          />
                          <div className="image-overlay">
                            <FaExpandAlt />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="col-12">
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="card-header bg-white py-2 px-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center">
                  <FaTasks className="text-primary ms-2" />
                  <h5 className="mb-0 fw-bold text-dark">المهام المرتبطة</h5>
                </div>
                <span className="badge bg-light text-dark border rounded-pill px-2 py-1 small">
                  {missionCount} مهمة
                </span>
              </div>
              <div className="card-body p-3 bg-white">
                {missionResults.length > 0 ? (
                  <div className="row g-3">
                    {missionResults.map((result) => {
                      const missionStatus = getMissionStatusMeta(result.status);
                      const assignedName = getAssignedName(result);
                      return (
                        <div
                          key={result.missionUid || result.missionId}
                          className="col-12 col-xl-6"
                        >
                          <div className="mission-result-card p-3 p-md-4 rounded-4 border h-100">
                            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                              <div>
                                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                  <h6 className="fw-bold text-dark mb-0">
                                    {result.missionType || "مهمة بدون عنوان"}
                                  </h6>
                                  <span
                                    className={`badge ${missionStatus.className}`}
                                  >
                                    {missionStatus.icon}
                                    {missionStatus.label}
                                  </span>
                                </div>
                                <div className="text-muted small d-flex flex-wrap align-items-center gap-2">
                                  <span>
                                    رقم المهمة:{" "}
                                    <strong>{result.missionId || "---"}</strong>
                                  </span>
                                  {assignedName && (
                                    <span className="mission-assignee">
                                      <FaUserShield className="ms-1" />
                                      الموكل بالمهمة{" "}
                                      <strong>
                                        {"  :  "}
                                        {assignedName}
                                      </strong>
                                    </span>
                                  )}
                                </div>
                              </div>
                              {result.linkedVisitId && (
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm rounded-pill px-3 fw-bold"
                                  onClick={() =>
                                    navigate(
                                      `/admin/visit-details/${result.linkedVisitId}`,
                                    )
                                  }
                                >
                                  <FaExternalLinkAlt className="ms-2" />
                                  فتح الزيارة
                                </button>
                              )}
                            </div>

                            <div className="row g-3 small mb-3">
                              <div className="col-4">
                                <div className="info-chip h-100">
                                  <div className="text-muted mb-1">
                                    تاريخ الإسناد
                                  </div>
                                  <div className="fw-bold text-dark">
                                    {formatDateTime(result.assignedAtUtc)}
                                  </div>
                                </div>
                              </div>
                              <div className="col-4">
                                <div className="info-chip h-100">
                                  <div className="text-muted mb-1">
                                    تاريخ الإنجاز
                                  </div>
                                  <div className="fw-bold text-dark">
                                    {formatDateTime(result.completedAtUtc)}
                                  </div>
                                </div>
                              </div>
                              <div className="col-4">
                                <div className="info-chip h-100">
                                  <div className="text-muted mb-1">
                                    نوع الزيارة
                                  </div>
                                  <div className="fw-bold text-dark">
                                    {result.visit?.visitType ||
                                      "لم تُنفَّذ بعد"}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="row g-3">
                              <div className="col-6">
                                <div className="p-3 rounded-4 bg-light border h-100">
                                  <div className="text-muted small fw-bold mb-2">
                                    تعليمات المهمة
                                  </div>
                                  <div className="text-dark lh-lg small">
                                    {result.instructions ||
                                      "لا توجد تعليمات مضافة."}
                                  </div>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="p-3 rounded-4 bg-light border h-100">
                                  <div className="text-muted small fw-bold mb-2">
                                    ملاحظات الإنجاز
                                  </div>
                                  <div className="text-dark lh-lg small">
                                    {result.completionNotes ||
                                      "لا توجد ملاحظات على الإنجاز."}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted bg-light rounded-3 border border-dashed small">
                    <FaTasks size={24} className="mb-2 opacity-50" />
                    <br />
                    لا توجد مهام مرتبطة بهذه الشكوى حتى الآن.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImg && (
        <div className="lightbox-overlay" onClick={() => setSelectedImg(null)}>
          <div
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-btn" onClick={() => setSelectedImg(null)}>
              <FaTimes />
            </button>
            <img
              src={selectedImg}
              alt="Evidence"
              className="img-fluid rounded-4 shadow-lg"
            />
          </div>
        </div>
      )}

      <ProcessComplaintModal
        isOpen={showProcessModal}
        onClose={() => setShowProcessModal(false)}
        onConfirm={handleProcess}
        loading={processLoading}
      />

      <style>{`
                .shadow-container { animation: fadeIn 0.5s ease; }
                .complaint-top-bar { border-start: 4px solid #0d6efd !important; }
                .establishment-link { color: #0d47a1; font-size: 1.05rem; }
                .establishment-link:hover { color: #1565c0; text-decoration: underline !important; }
                .complaint-status-pill { font-size: 0.78rem; font-weight: 700; }
                .complaint-status-pill.is-processed { background: #e6f4ea; color: #137333; border: 1px solid #b7dfc2; }
                .complaint-status-pill.is-pending { background: #fff3cd; color: #856404; border: 1px solid #ffe08a; }
                .complaint-text { white-space: pre-line; text-align: justify; font-size: 0.95rem; }
                .complaint-quote { background: #fdfdfd; border: 1px solid #f0f0f0; }
                .complaint-quote::before { content: '"'; position: absolute; top: -8px; right: 16px; font-size: 56px; color: #007bff; opacity: 0.1; font-family: serif; }
                .submitter-phone-badge {
                    display: inline-flex; align-items: center;
                    background: rgba(13, 110, 253, 0.08);
                    color: #0d47a1;
                    border: 1px solid rgba(13, 110, 253, 0.2);
                    border-radius: 999px;
                    padding: 0.2rem 0.65rem;
                    font-size: 0.82rem;
                    font-weight: 800;
                    letter-spacing: 0.5px;
                }
                .image-card { cursor: pointer; position: relative; height: 180px; transition: 0.3s; }
                .image-card--compact { height: 110px; }
                .image-card img { width: 100%; height: 100%; object-fit: cover; }
                .image-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; color: white; font-size: 1.8rem; opacity: 0; transition: 0.3s; }
                .image-card:hover .image-overlay { opacity: 1; }
                .image-card:hover { transform: translateY(-5px); }
                .lightbox-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.92); z-index: 10001; display: flex; align-items: center; justify-content: center; padding: 20px; backdrop-filter: blur(5px); }
                .lightbox-content { max-width: 80%; max-height: 80vh; position: relative; }
                .close-btn { position: absolute; top: -50px; left: 0; background: none; border: none; color: white; font-size: 2rem; cursor: pointer; }
                .x-small { font-size: 0.7rem; }
                .bg-success-subtle { background-color: #e6f4ea !important; }
                .bg-warning-subtle { background-color: #fff3cd !important; }
                .bg-danger-subtle { background-color: #f8d7da !important; }
                .bg-primary-subtle { background-color: #e7f1ff !important; }
                .bg-secondary-subtle { background-color: #e9ecef !important; }
                .tracking-wider { letter-spacing: 1px; }
                .mission-result-card { background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%); }
                .mission-assignee {
                    display: inline-flex;
                    align-items: center;
                    color: #0d47a1;
                    background: rgba(13, 110, 253, 0.08);
                    border: 1px solid rgba(13, 110, 253, 0.18);
                    border-radius: 999px;
                    padding: 0.1rem 0.55rem;
                    font-weight: 600;
                }
                .info-chip { background: #fff; border: 1px solid #edf0f4; border-radius: 1rem; padding: 0.85rem 1rem; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
    </div>
  );
}
